import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';

import { uploadWatermark } from '../../actions';
import { hasTransparentBackground } from '../../../Overlays/helpers';
import { displayErrorModal } from '../../../../../helpers/swal';

const MAX_FILE_SIZE_BYTES = 1024 * 1024; // 1MB

const AddWatermarkButton = ({
  watermarks = [],
  slugDate,
  uploadWatermarkAction,
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const clearInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const onSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file || !slugDate) return;

    const ext = (file.name || '').toLowerCase().slice(-5);
    const isPng = ext.endsWith('.png');
    const isWebp = ext.endsWith('.webp');
    if (!isPng && !isWebp) {
      displayErrorModal({ text: 'File must be PNG or WebP with transparent background.' });
      clearInput();
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      displayErrorModal({ text: `File must be 1 MB or less (${(file.size / 1024).toFixed(0)} KB selected).` });
      clearInput();
      return;
    }

    try {
      const hasAlpha = await hasTransparentBackground(file);
      if (!hasAlpha) {
        displayErrorModal({
          title: 'Image not transparent',
          text: 'Please use PNG or WebP with a transparent background.',
        });
        clearInput();
        return;
      }
    } catch (err) {
      displayErrorModal({ text: err?.message || 'Failed to validate image.' });
      clearInput();
      return;
    }

    if (watermarks.length >= 4) {
      displayErrorModal({ text: 'Maximum 4 watermarks allowed.' });
      clearInput();
      return;
    }

    setUploading(true);
    try {
      const index = watermarks.length;
      await uploadWatermarkAction(index, file);
    } catch {
      // Error already shown by action
    } finally {
      setUploading(false);
      clearInput();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/webp,.png,.webp"
        className="d-none"
        onChange={onSelect}
      />
      <Button
        variant="outline-primary"
        type="button"
        className="small text-nowrap"
        disabled={!slugDate || uploading || watermarks.length >= 4}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? 'Uploading…' : 'Add watermark'}
      </Button>
    </>
  );
};

export default connect(
  (state) => {
    const sections = state.graphics?.sections || [];
    const watermarksSection = sections.find((s) => s.key === 'watermarks');
    const watermarks = watermarksSection?.data?.watermarks || [];
    const slugDate = state.events?.selectedEvents?.[0]?.slugDate;
    return { watermarks, slugDate };
  },
  { uploadWatermarkAction: uploadWatermark },
)(AddWatermarkButton);
